import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import { toast } from 'react-toastify';

const RentalDurationList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [rentalSlots, setRentalSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const fetchRentalSlots = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/RentParkingSlot/GetAllRentParkingDetails`
            });

            if (response.isSuccess || response.success || response.IsSuccess) {
                let apiData = response.data;
                let data = [];
                if (Array.isArray(apiData)) {
                    data = apiData;
                } else if (apiData && Array.isArray(apiData.data)) {
                    data = apiData.data;
                } else if (apiData && Array.isArray(apiData.RentParkingData)) {
                    data = apiData.RentParkingData;
                }
                
                setRentalSlots(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching rental slots:", error);
            toast.error("Failed to fetch rental slots.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRentalSlots();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const onPageChange = (page) => {
        setCurrentPage(page);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "SITE", key: "SiteName" },
        { label: "WING", key: "WingName" },
        { label: "FLOOR", key: "FloorName" },
        { label: "UNIT", key: "UnitName" },
        { label: "SLOT NAME", key: "SlotName" },
        { label: "RENT PERIOD", key: "RentPeriodName" },
        { label: "START DATE", key: "StartDate" },
        { label: "END DATE", key: "EndDate" },
        { label: "STATUS", key: "IsActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const d = new Date(dateString);
        return d.toLocaleString('en-US', { 
            month: 'short', day: 'numeric', year: 'numeric', 
            hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true 
        });
    };

    const renderCell = (key, row) => {
        switch (key) {
            case "no":
            case "SlotName":
                return <span className="text-xs font-semibold text-[#1E293B]">{row[key] || "—"}</span>;
            case "SiteName":
            case "WingName":
            case "FloorName":
            case "UnitName":
            case "RentPeriodName":
                return <span className="text-xs font-medium text-slate-700">{row[key] || "—"}</span>;
            case "StartDate":
            case "EndDate":
                return <span className="text-xs font-medium text-slate-700">{formatDate(row[key])}</span>;
            case "IsActive":
                let statusColor = "bg-gray-100 text-gray-600";
                const activeVal = row.IsActive || row.isActive;
                if (activeVal === 'Active' || activeVal === 1) statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (activeVal === 'Inactive' || activeVal === 0) statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {(activeVal === 1 ? 'Active' : activeVal === 0 ? 'Inactive' : activeVal) || "—"}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };


    // Filter by search query and status tab
    const safeData = Array.isArray(rentalSlots) ? rentalSlots : [];
    const filteredData = safeData.filter(item => {
        let matchesSearch = true;
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            matchesSearch = (
                (item.UnitName || "").toLowerCase().includes(query) ||
                (item.SlotName || "").toLowerCase().includes(query) ||
                (item.SiteName || "").toLowerCase().includes(query)
            );
        }
        
        let matchesStatus = true;
        if (statusFilter) {
            matchesStatus = item.IsActive === statusFilter;
        }

        return matchesSearch && matchesStatus;
    });

    const paginatedData = filteredData.slice((currentPage - 1) * pageInfo.pageSize, currentPage * pageInfo.pageSize).map((item, idx) => ({
        ...item,
        id: item.RentId || item.rentId || `rs-${idx}`,
        no: (currentPage - 1) * pageInfo.pageSize + idx + 1
    }));

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <SearchBar
                onSearch={setSearchQuery}
                placeholder="Search slot/unit..."
                className="w-full sm:w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    const handleEdit = (row) => {
        navigate('/zentcomm/parking-management/rent-Type/form', { state: { recordData: row } });
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <PageHeadingSection
                Navbar={true}
                mainHeading="Duration of Rental"
                subHeading="Manage all temporary parking rent allocations"
                primaryBtnText="Add New Slot"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => {
                    navigate('/zentcomm/parking-management/rent-Type/form');
                }}
            />

            <CommonTable
                tableHeading="Rent Parking Slots"
                headerAction={headerAction}
                data={paginatedData}
                headers={tableHeaders}
                renderCell={renderCell}
                pagination={{
                    ...pageInfo,
                    pageNumber: currentPage,
                    totalRecords: filteredData.length,
                    totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                }}
                onPageChange={onPageChange}
                onLimitChange={handleLimitChange}
                loading={loading}
                showActionCol={true}
                onEdit={handleEdit}
                hideDelete={true}
            />
        </div>
    );
};

export default RentalDurationList;
